import { readFileSync, writeFileSync, existsSync, mkdirSync, renameSync } from "node:fs";
import { join, dirname } from "node:path";
import { minimatch } from "minimatch";
import { stringify, parse } from "yaml";
import { isSensitiveFile, scanForCredentials } from "./credentials.js";
import type {
    DismissalRecord,
    GenerationLock,
    GenerationRecord,
    StoredPatch,
    CustomizationsConfig,
} from "./types.js";

const LOCKFILE_HEADER = "# DO NOT EDIT MANUALLY - Managed by Fern Replay\n";

export class LockfileNotFoundError extends Error {
    constructor(path: string) {
        super(`Lockfile not found: ${path}`);
        this.name = "LockfileNotFoundError";
    }
}

export class LockfileManager {
    private outputDir: string;
    private lock: GenerationLock | null = null;
    private fernignorePatterns: string[] = [];
    readonly warnings: string[] = [];

    constructor(outputDir: string) {
        this.outputDir = outputDir;
    }

    /**
     * Inform the lockfile of the customer's `.fernignore` patterns so that
     * `save()` can suppress its "Patch X removed from lockfile" warning when
     * every file in the stripped patch is already `.fernignore`-protected.
     * The strip itself still happens (credentials never persist); only the
     * customer-visible warning is suppressed in that case, because the
     * protected files survive the generator wipe regardless of whether the
     * lockfile tracks them, so there's nothing for the customer to action.
     */
    setFernignorePatterns(patterns: string[]): void {
        this.fernignorePatterns = patterns;
    }

    get lockfilePath(): string {
        return join(this.outputDir, ".fern", "replay.lock");
    }

    get customizationsPath(): string {
        return join(this.outputDir, ".fern", "replay.yml");
    }

    exists(): boolean {
        return existsSync(this.lockfilePath);
    }

    read(): GenerationLock {
        if (this.lock) {
            return this.lock;
        }
        try {
            const content = readFileSync(this.lockfilePath, "utf-8");
            this.lock = parse(content) as GenerationLock;
            return this.lock;
        } catch (error: unknown) {
            if (
                error instanceof Error &&
                "code" in error &&
                (error as NodeJS.ErrnoException).code === "ENOENT"
            ) {
                throw new LockfileNotFoundError(this.lockfilePath);
            }
            throw error;
        }
    }

    initialize(firstGeneration: GenerationRecord): void {
        this.initializeInMemory(firstGeneration);
        this.save();
    }

    /**
     * Set up in-memory lock state without writing to disk.
     * Useful for bootstrap dry-run where we need state for detection
     * but don't want to persist anything.
     */
    initializeInMemory(firstGeneration: GenerationRecord): void {
        this.lock = {
            version: "1.0",
            generations: [firstGeneration],
            current_generation: firstGeneration.commit_sha,
            patches: [],
        };
    }

    save(): void {
        if (!this.lock) {
            throw new Error("No lockfile data to save. Call read() or initialize() first.");
        }

        // Clear warnings from any previous save() call to prevent accumulation.
        this.warnings.length = 0;

        // Layer 2 credential safety net: scan all patches before writing.
        // Strips ENTIRE patches that contain credential content (in either
        // `patch_content` or `theirs_snapshot`) or reference sensitive files.
        // Snapshot scanning is essential — snapshots store full file content,
        // so they're strictly more credential-rich than the diff alone.
        // Without scanning snapshots, a customer-edited config with a
        // committed token would leak into `.fern/replay.lock` via the
        // snapshot field even though the patch_content scan caught nothing.
        //
        // Note: if a patch modifies both a sensitive file and a legitimate file, the entire
        // patch is dropped. This is a deliberate safety trade-off — partial redaction risks
        // leaving credential context that regex didn't catch. The customer gets a warning.
        const cleanPatches: StoredPatch[] = [];
        for (const patch of this.lock.patches) {
            const diffMatches = scanForCredentials(patch.patch_content);
            const snapshotMatches: string[] = [];
            if (patch.theirs_snapshot) {
                for (const content of Object.values(patch.theirs_snapshot)) {
                    const m = scanForCredentials(content);
                    for (const x of m) {
                        if (!snapshotMatches.includes(x)) snapshotMatches.push(x);
                    }
                }
            }
            const credentialMatches = [...diffMatches, ...snapshotMatches.filter((m) => !diffMatches.includes(m))];
            const sensitiveFiles = patch.files.filter((f) => isSensitiveFile(f));

            if (credentialMatches.length > 0 || sensitiveFiles.length > 0) {
                const reasons: string[] = [];
                if (credentialMatches.length > 0) {
                    reasons.push(`credential patterns: ${credentialMatches.join(", ")}`);
                }
                if (sensitiveFiles.length > 0) {
                    reasons.push(`sensitive files: ${sensitiveFiles.join(", ")}`);
                }
                // Suppress the customer-visible warning when every file in
                // the patch is `.fernignore`-protected. Such files survive
                // the generator wipe by themselves; the lockfile strip is a
                // background safety measure with no customer-visible
                // consequence. The strip itself still happens.
                const allProtected = patch.files.length > 0 && patch.files.every((f) =>
                    this.fernignorePatterns.some((p) => f === p || minimatch(f, p))
                );
                if (!allProtected) {
                    this.warnings.push(
                        `Patch ${patch.id} removed from lockfile: ${reasons.join("; ")}. ` +
                        `This prevents credential exposure in committed lockfiles.`
                    );
                }
            } else {
                cleanPatches.push(patch);
            }
        }
        this.lock.patches = cleanPatches;

        const dir = dirname(this.lockfilePath);
        if (!existsSync(dir)) {
            mkdirSync(dir, { recursive: true });
        }
        const yaml = stringify(this.lock, {
            lineWidth: 0,
            blockQuote: "literal",
        });
        const content = LOCKFILE_HEADER + yaml;
        // Atomic write: write to temp file then rename (rename is atomic on most filesystems)
        const tmpPath = this.lockfilePath + ".tmp";
        writeFileSync(tmpPath, content, "utf-8");
        renameSync(tmpPath, this.lockfilePath);
    }

    addGeneration(record: GenerationRecord): void {
        this.ensureLoaded();
        this.lock!.generations.push(record);
        this.lock!.current_generation = record.commit_sha;
    }

    addPatch(patch: StoredPatch): void {
        this.ensureLoaded();
        this.lock!.patches.push(patch);
    }

    updatePatch(
        patchId: string,
        updates: Partial<Pick<StoredPatch, "base_generation" | "patch_content" | "content_hash" | "files" | "status" | "user_owned" | "theirs_snapshot">>,
    ): void {
        this.ensureLoaded();
        const patch = this.lock!.patches.find((p) => p.id === patchId);
        if (!patch) {
            throw new Error(`Patch not found: ${patchId}`);
        }
        Object.assign(patch, updates);
    }

    removePatch(patchId: string): void {
        this.ensureLoaded();
        this.lock!.patches = this.lock!.patches.filter((p) => p.id !== patchId);
    }

    clearPatches(): void {
        this.ensureLoaded();
        this.lock!.patches = [];
    }

    addForgottenHash(hash: string): void {
        this.ensureLoaded();
        if (!this.lock!.forgotten_hashes) {
            this.lock!.forgotten_hashes = [];
        }
        if (!this.lock!.forgotten_hashes.includes(hash)) {
            this.lock!.forgotten_hashes.push(hash);
        }
    }

    /**
     * Record a dismissed customization: visibility metadata in `dismissals`
     * plus a tombstone in `forgotten_hashes` for every content hash.
     * `forgotten_hashes` stays the only list the detector consults; the
     * metadata exists so `status` can show what was dismissed.
     */
    addDismissal(record: DismissalRecord): void {
        this.ensureLoaded();
        if (!this.lock!.dismissals) {
            this.lock!.dismissals = [];
        }
        this.lock!.dismissals.push(record);
        for (const hash of record.content_hashes) {
            this.addForgottenHash(hash);
        }
    }

    getUnresolvedPatches(): StoredPatch[] {
        this.ensureLoaded();
        return this.lock!.patches.filter((p) => p.status === "unresolved");
    }

    getResolvingPatches(): StoredPatch[] {
        this.ensureLoaded();
        return this.lock!.patches.filter((p) => p.status === "resolving");
    }

    markPatchUnresolved(patchId: string): void {
        this.updatePatch(patchId, { status: "unresolved" });
    }

    markPatchResolved(
        patchId: string,
        updates: Pick<StoredPatch, "patch_content" | "content_hash" | "base_generation" | "files" | "theirs_snapshot">,
    ): void {
        this.ensureLoaded();
        const patch = this.lock!.patches.find((p) => p.id === patchId);
        if (!patch) {
            throw new Error(`Patch not found: ${patchId}`);
        }
        delete patch.status;
        Object.assign(patch, updates);
    }

    getPatches(): StoredPatch[] {
        this.ensureLoaded();
        return this.lock!.patches;
    }

    setReplaySkippedAt(timestamp: string): void {
        this.ensureLoaded();
        this.lock!.replay_skipped_at = timestamp;
    }

    clearReplaySkippedAt(): void {
        this.ensureLoaded();
        delete this.lock!.replay_skipped_at;
    }

    isReplaySkipped(): boolean {
        this.ensureLoaded();
        return this.lock!.replay_skipped_at != null;
    }

    getGeneration(commitSha: string): GenerationRecord | undefined {
        this.ensureLoaded();
        return this.lock!.generations.find((g) => g.commit_sha === commitSha);
    }

    getCustomizationsConfig(): CustomizationsConfig {
        if (!existsSync(this.customizationsPath)) {
            return {};
        }
        const content = readFileSync(this.customizationsPath, "utf-8");
        return (parse(content) as CustomizationsConfig) ?? {};
    }

    private ensureLoaded(): void {
        if (!this.lock) {
            throw new Error("No lockfile loaded. Call read() or initialize() first.");
        }
    }
}
